import React, { useState, useMemo } from "react";

import { Divider, useTheme, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import HistogramAggSelector from "components/widgetParts/HistogramAggSelector";
import { Agg } from "system/query/Aggs";
import { QueryField, QueryFieldTypes, QueryInfo, socialUniverseField } from "system/query/QueryInfo";
import TrendChartGraphPreview from "components/widgets/charts/TrendChart/TrendChartGraphPreview";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsCheckbox from "components/settingsConfig/uiComponents/SettingsCheckbox";
import SettingsSlider from "components/settingsConfig/uiComponents/SettingsSlider";
import SettingsSingleSelect from "components/settingsConfig/uiComponents/SettingsSingleSelect";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import { OptionsValueType } from "components/settingsConfig/uiComponents/SettingsSingleSelect";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetRequest } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";

const defaultWidgetRequest = {
  name: 'trend-chart',
  endpoint: 'query/agg',
  queryParams: {
    flatten: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_MULTI_WHEN_ALL
} as WidgetRequest;

export function BuildDefaultTrendChartRequests(queryInfo: QueryInfo): WidgetRequest[] | undefined {
  const foundTimestampField = queryInfo.fields?.find((field:QueryField) => field.type === QueryFieldTypes.timestamp) || false;
  const foundSocialUniverseField = queryInfo.fields?.find((field: QueryField) => field.id === socialUniverseField.id) || false;

  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };

  const requestInitBody = { aggs: {} };
  if (foundTimestampField) {
    requestInitBody.aggs = {
      histogram: {
        op: "histogram",
        field: foundTimestampField.id,
        ...(foundSocialUniverseField && {
          aggs: {
            [socialUniverseField.label]: {
              op: socialUniverseField.id
            }
          }
        }),
      }
    };
  } else {
    return undefined;
  }

  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}

export type TrendChartSettingsStateType = {
  widgetTitle: string;
  customWidgetTitle: string;
  showDots: boolean;
  showGrid: boolean;
  lineWidth: number;
  lineType: string;
  lineColor?: string;
  showLegend: boolean;
  horizontalAlign: string;
  verticalAlign: string;
  xAxis: boolean;
  yAxis: boolean;
  showEvents: boolean;
  widgetTitleAlignment: string;
};

export const defaultSettings: TrendChartSettingsStateType = {
  widgetTitle: 'default',
  customWidgetTitle: '',
  showDots: true,
  showGrid: true,
  lineWidth: 3,
  lineType: 'monotone',
  showLegend: true,
  horizontalAlign: 'center',
  verticalAlign: 'bottom',
  xAxis: true,
  yAxis: true,
  showEvents: true,
  widgetTitleAlignment: 'left',
};

export default function TrendChartSettings(props: WidgetSettingsProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'trend-chart');
  const requestBody = originalRequest?.requestInit?.body || {};

  const [localAggs, setLocalAggs] = useState<Agg>(requestBody?.aggs?.histogram);

  const updateWidget = () => {
    const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
    const requestInitBody = {
      aggs: {
        histogram: localAggs
      }
    };

    widgetRequest.requestInit = { body: requestInitBody };

    props.saveWidgetDataAndRequests({
      settings: settingsState
    }, [widgetRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    });
  };

  const chartColors: string[] = [
    theme.colors.namedColors.chart1,
    theme.colors.namedColors.chart2,
    theme.colors.namedColors.chart3,
    theme.colors.namedColors.chart4,
    theme.colors.namedColors.chart5,
    theme.colors.namedColors.chart6,
    theme.colors.namedColors.chart7,
    theme.colors.namedColors.chart8,
    theme.colors.namedColors.chart9,
  ];

  const lineTypeOptions: OptionsValueType[] = useMemo(() => ([
      { label: i18n.t("widget-types.trend-chart.settings.monotone"), value: 'monotone' },
      { label: i18n.t("widget-types.trend-chart.settings.linear"), value: 'linear' },
      { label: i18n.t("widget-types.trend-chart.settings.step"), value: 'step' },
    ]), []);

  const verticalAlignOptions: OptionsValueType[] = useMemo(() => ([
      { label: i18n.t("bottom"), value: 'bottom' },
      { label: i18n.t("top"), value: 'top' },
    ]), []);

  // The Trend Chart was originally saving data using a kebob case (e.g. show-dots).  This was
  // changed to camel case (e.g. showDots).  To solve this we are merging both settings together to
  // preserve the original settings and then inserting the new settings defaults so that the UI can
  // use the new camel case settings.
  const updatedDefaultSettings = {...defaultSettings, lineColor: chartColors[0], ...props?.widget?.data?.settings};

  const [settingsState, setSettingsState] = useState<TrendChartSettingsStateType>(updatedDefaultSettings);
  const settingsOnChange = (updatedState: TrendChartSettingsStateType) => {
    setSettingsState(updatedState);
  };

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={['Query & Data', 'Display']}
      title={i18n.t("widget-types.trend-chart.settings.title")}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 && (
          <>
          {props.queryInfo &&
            <HistogramAggSelector
              aggs={localAggs}
              queryInfo={props.queryInfo}
              setAggs={(aggs:Agg) => setLocalAggs(aggs)} />
          }
          </>
        )}
        {/* Tab #2 */}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            {/* Widget Graph Preview */}
            <TrendChartGraphPreview settingsState={settingsState} />

            {/* Settings Options */}
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={`${i18n.t("widget")} ${i18n.t("title")}`} showBottomDivider={true}>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label="Widget Title"
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                  {settingsState.widgetTitle === 'custom' &&
                    <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                      <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                      <SettingsTextField
                        keyid="customWidgetTitle"
                        label="Custom Text"
                        value={settingsState.customWidgetTitle}
                      />
                    </Stack>}
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment} />
              </SettingsAccordion>

              <SettingsAccordion label={i18n.t("graph")} showBottomDivider={true}>
                <SettingsCheckbox
                  checked={settingsState.showDots}
                  keyid="showDots"
                  label={i18n.t("widget-settings.show-dots")} />
                <SettingsCheckbox
                  checked={settingsState.showGrid}
                  keyid="showGrid"
                  label={i18n.t("widget-types.trend-chart.settings.show-grid")} />
                <SettingsSlider
                  defaultValue={3}
                  keyid="lineWidth"
                  label={i18n.t("widget-types.trend-chart.settings.line-width")}
                  max={7}
                  min={1}
                  sliderValue={settingsState.lineWidth} />
                <SettingsSingleSelect
                  defaultValue={lineTypeOptions.find((item: OptionsValueType) => (
                    item.value === 'monotone'
                  )) ?? lineTypeOptions[0]}
                  keyid="lineType"
                  label={i18n.t("widget-types.trend-chart.settings.line-type")}
                  value={lineTypeOptions.find((item: OptionsValueType) => (
                    item.value === settingsState.lineType
                  ))}
                  options={lineTypeOptions} />
              </SettingsAccordion>

              {/* Legend Settings */}
              <SettingsAccordion label={i18n.t("legend")} showBottomDivider={true}>
                <SettingsCheckbox
                  keyid="showLegend"
                  label={i18n.t("widget-settings.show-legend")}
                  checked={settingsState.showLegend} />
                {settingsState.showLegend &&
                  <>
                    <SettingsToggleButton
                      keyid="horizontalAlign"
                      label={i18n.t("widget-types.trend-chart.settings.horizontal-align")}
                      optionsList={horizontalAlignmentOptions}
                      value={settingsState.horizontalAlign} />
                    <SettingsSingleSelect
                      keyid="verticalAlign"
                      label={i18n.t("widget-types.trend-chart.settings.vertical-align")}
                      defaultValue={verticalAlignOptions.find((item: OptionsValueType) => (
                        item.value === 'center'
                      )) ?? verticalAlignOptions[0]}
                      value={verticalAlignOptions.find((item: OptionsValueType) => (
                        item.value === settingsState.verticalAlign
                      ))}
                      options={verticalAlignOptions} />
                  </>
                }
              </SettingsAccordion>
              <SettingsAccordion label={i18n.t("axis")} showBottomDivider={true}>
                <SettingsCheckbox
                  keyid="xAxis"
                  label={i18n.t("x-axis")}
                  checked={settingsState.xAxis} />
                <SettingsCheckbox
                  keyid="yAxis"
                  label={i18n.t("y-axis")}
                  checked={settingsState.yAxis} />

              </SettingsAccordion>
              <SettingsAccordion label={i18n.t("widget-types.trend-chart.settings.trend-events")}>
                <SettingsCheckbox
                  keyid="showEvents"
                  label={i18n.t("widget-types.trend-chart.settings.show-events")}
                  checked={settingsState.showEvents} />
              </SettingsAccordion>
            </SettingsOptions>
        </SplitSettingsWrapper>}
      </>
    </TabbedDialog>
  );
}